import { CAMERA_STYLE, PLAYER_RADIUS, TILE, type Vec2 } from '@chessshooter/shared';

export interface Viewport {
  width: number;
  height: number;
}

/** Where the world rectangle lands on screen (CSS px) and how many px one world unit is. */
export interface Camera {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
}

/**
 * Letterbox: the largest copy of a worldW x worldH rectangle that fits the viewport below a top
 * inset (the HUD strip), centred in the remaining space. Works for portrait and landscape alike.
 */
export function fitWorld(vp: Viewport, worldW: number, worldH: number, topInset = 0, margin = 0): Camera {
  const availW = Math.max(1, vp.width - 2 * margin);
  const availH = Math.max(1, vp.height - topInset - 2 * margin);
  const scale = Math.min(availW / worldW, availH / worldH);
  const width = worldW * scale;
  const height = worldH * scale;
  return {
    x: margin + (availW - width) / 2,
    y: topInset + margin + (availH - height) / 2,
    width,
    height,
    scale,
  };
}

export function worldToScreen(cam: Camera, p: Vec2): Vec2 {
  return { x: cam.x + p.x * cam.scale, y: cam.y + p.y * cam.scale };
}

export function screenToWorld(cam: Camera, p: Vec2): Vec2 {
  return { x: (p.x - cam.x) / cam.scale, y: (p.y - cam.y) / cam.scale };
}

/** A readable player size on small screens; the camera stops at the arena edges. */
export function followWorld(vp: Viewport, worldW: number, worldH: number, focus: Vec2, topInset: number = CAMERA_STYLE.hudHeight, overview = false): Camera {
  const fit = fitWorld(vp, worldW, worldH, topInset, CAMERA_STYLE.margin);
  if (overview || fit.scale * PLAYER_RADIUS * 2 >= CAMERA_STYLE.overviewMinPlayerDiameter) return fit;
  const availableHeight = Math.max(1, vp.height - topInset);
  const readableScale = Math.min(
    CAMERA_STYLE.targetPlayerDiameter / (PLAYER_RADIUS * 2),
    Math.min(vp.width, availableHeight) / (CAMERA_STYLE.minVisibleTiles * TILE),
  );
  const scale = Math.max(fit.scale, readableScale);
  const width = worldW * scale;
  const height = worldH * scale;
  const axis = (size: number, available: number, target: number): number =>
    size <= available ? (available - size) / 2 : Math.max(available - size, Math.min(0, available / 2 - target * scale));
  return { x: axis(width, vp.width, focus.x), y: topInset + axis(height, availableHeight, focus.y), width, height, scale };
}
